import { Control } from 'react-hook-form'
import {
	ComparisonOperator,
	PromotionType,
	DayOfWeek,
	DepositHistoryPeriod,
} from '../../type'
import {
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from '@/common/ui/form'
import { Checkbox } from '@/common/ui/checkbox'
import { Input } from '@/common/ui/input'
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/common/ui/select'
import { RadioGroup, RadioGroupItem } from '@/common/ui/radio-group'
import { Label } from '@/common/ui/label'
import {
	promotionTypeMapper,
	dayOfWeekMapper,
	depositHistoryPeriodMapper,
} from '../../mappers'

interface ConstraintSelectorProps {
	control: Control<any>
	name: string
	type?: 'deposit' | 'luckywheel'
}

type ConstraintType = {
	constraintType: PromotionType
	operator?: ComparisonOperator
	amount?: number | null
	days?: DayOfWeek[]
	rank?: string
	isNewUser?: boolean
	isFirstDeposit?: boolean
	isFirstTimeOfDay?: boolean
	turnoverAmount?: number
	period?: DepositHistoryPeriod
}

const preventMinusInput = (e: React.KeyboardEvent<HTMLInputElement>) => {
	if (e.key === '-' || e.key === 'e') {
		e.preventDefault()
	}
}

export const ConstraintSelector = ({
	control,
	name,
	type = 'deposit',
}: ConstraintSelectorProps) => {
	const availableTypes =
		type === 'luckywheel'
			? [
					PromotionType.LOSS_AMOUNT,
					PromotionType.DEPOSIT_AMOUNT,
					PromotionType.TURNOVER,
				]
			: [
					PromotionType.NEW_USER,
					PromotionType.FIRST_DEPOSIT,
					PromotionType.FIRST_TIME_OF_DAY,
					PromotionType.DEPOSIT_AMOUNT,
					PromotionType.DAY_OF_WEEK,
					PromotionType.DEPOSIT_HISTORY_AMOUNT,
				]

	return (
		<FormField
			control={control}
			name={name}
			render={({ field, fieldState }) => (
				<FormItem className="rounded-lg border p-4">
					<FormLabel>เลือกประเภทของโปรโมชั่น</FormLabel>
					<div className="space-y-4">
						<div className="grid grid-cols-3 gap-4">
							{availableTypes.map((type) => (
								<FormItem key={type} className="flex items-center gap-2">
									<FormControl>
										<Checkbox
											checked={field.value.some(
												(constraint: ConstraintType) =>
													constraint.constraintType === type,
											)}
											onCheckedChange={(checked) => {
												if (checked) {
													const newConstraint = {
														constraintType: type,
														...(type === PromotionType.NEW_USER && {
															isNewUser: true,
														}),
														...(type === PromotionType.FIRST_DEPOSIT && {
															isFirstDeposit: true,
														}),
														...(type === PromotionType.FIRST_TIME_OF_DAY && {
															isFirstTimeOfDay: true,
														}),
														...(type === PromotionType.DEPOSIT_AMOUNT && {
															operator: ComparisonOperator.GREATER_THAN,
															amount: 0,
														}),
														...(type === PromotionType.LOSS_AMOUNT && {
															operator: ComparisonOperator.GREATER_THAN,
															amount: 0,
														}),
														...(type === PromotionType.DAY_OF_WEEK && {
															days: [] as DayOfWeek[],
														}),
														...(type === PromotionType.TURNOVER && {
															amount: 0,
														}),
														...(type ===
															PromotionType.DEPOSIT_HISTORY_AMOUNT && {
															period: DepositHistoryPeriod.TODAY,
															operator:
																ComparisonOperator.GREATER_THAN_OR_EQUAL,
															amount: 0,
														}),
													} as ConstraintType
													field.onChange([...field.value, newConstraint])
												} else {
													field.onChange(
														field.value.filter(
															(constraint: ConstraintType) =>
																constraint.constraintType !== type,
														),
													)
												}
											}}
										/>
									</FormControl>
									<FormLabel className="font-normal">
										{promotionTypeMapper[type]}
									</FormLabel>
								</FormItem>
							))}
						</div>

						{field.value.map((constraint: ConstraintType, index: number) => {
							if (
								constraint.constraintType === PromotionType.DEPOSIT_AMOUNT ||
								constraint.constraintType === PromotionType.LOSS_AMOUNT
							) {
								return (
									<div key={index} className="flex items-end gap-4">
										<FormItem className="flex-1">
											<FormLabel>
												{constraint.constraintType === PromotionType.LOSS_AMOUNT
													? 'เงื่อนไขจำนวนยอดเสีย'
													: 'เงื่อนไขจำนวนฝาก'}
											</FormLabel>
											<Select
												value={constraint.operator}
												onValueChange={(value: ComparisonOperator) => {
													const newValue = [...field.value]
													newValue[index] = {
														...constraint,
														operator: value,
													}
													field.onChange(newValue)
												}}
											>
												<SelectTrigger>
													<SelectValue placeholder="เลือกเงื่อนไข" />
												</SelectTrigger>
												<SelectContent>
													<SelectItem value={ComparisonOperator.GREATER_THAN}>
														มากกว่า
													</SelectItem>
													<SelectItem
														value={ComparisonOperator.GREATER_THAN_OR_EQUAL}
													>
														มากกว่าหรือเท่ากับ
													</SelectItem>
												</SelectContent>
											</Select>
										</FormItem>

										<FormItem className="flex-1">
											<FormLabel>
												{constraint.constraintType === PromotionType.LOSS_AMOUNT
													? 'จำนวนยอดเสีย'
													: 'จำนวนฝาก'}
											</FormLabel>
											<Input
												type="number"
												onKeyDown={preventMinusInput}
												value={constraint.amount ?? ''}
												onChange={(e) => {
													const newValue = [...field.value]
													newValue[index] = {
														...constraint,
														amount: e.target.value
															? Number(e.target.value)
															: null,
													}
													field.onChange(newValue)
												}}
												placeholder={
													constraint.constraintType ===
													PromotionType.LOSS_AMOUNT
														? 'กรอกจำนวนยอดเสีย'
														: 'กรอกจำนวนฝาก'
												}
											/>
										</FormItem>
									</div>
								)
							}

							if (constraint.constraintType === PromotionType.TURNOVER) {
								return (
									<div key={index} className="space-y-2">
										<FormItem className="flex-1">
											<FormLabel>จำนวนเทิร์นโอเวอร์ (บาท)</FormLabel>
											<Input
												type="number"
												onKeyDown={preventMinusInput}
												value={constraint.amount ?? ''}
												onChange={(e) => {
													const newValue = [...field.value]
													newValue[index] = {
														...constraint,
														amount: Number(e.target.value),
													}
													field.onChange(newValue)
												}}
												placeholder="กรอกจำนวนเทิร์นโอเวอร์ (บาท)"
											/>
										</FormItem>
									</div>
								)
							}

							if (constraint.constraintType === PromotionType.DAY_OF_WEEK) {
								return (
									<div key={index} className="space-y-2">
										<FormLabel>เลือกวันสำหรับการใช้โปรโมชั่น</FormLabel>
										<div className="grid grid-cols-4 gap-4">
											{Object.entries(DayOfWeek).map(([day]) => (
												<FormItem key={day} className="flex items-center gap-2">
													<FormControl>
														<Checkbox
															checked={constraint?.days?.includes(
																day as DayOfWeek,
															)}
															onCheckedChange={(checked) => {
																const newValue = [...field.value]
																newValue[index] = {
																	...constraint,
																	days: checked
																		? [
																				...(constraint?.days || []),
																				day as DayOfWeek,
																			]
																		: (constraint?.days || []).filter(
																				(d) => d !== day,
																			),
																}
																field.onChange(newValue)
															}}
														/>
													</FormControl>
													<FormLabel className="font-normal">
														{dayOfWeekMapper[day as DayOfWeek]}
													</FormLabel>
												</FormItem>
											))}
										</div>
									</div>
								)
							}

							if (constraint.constraintType === PromotionType.FIRST_DEPOSIT) {
								return (
									<div key={index} className="space-y-2">
										<FormLabel>เคย/ไม่เคยฝากเงินครั้งแรก</FormLabel>
										<RadioGroup
											value={constraint.isFirstDeposit ? 'true' : 'false'}
											onValueChange={(value) => {
												const newValue = [...field.value]
												newValue[index] = {
													...constraint,
													isFirstDeposit: value === 'true',
												}
												field.onChange(newValue)
											}}
										>
											<div className="flex items-center space-x-2">
												<RadioGroupItem
													value="false"
													id={`first-deposit-false-${index}`}
												/>
												<Label htmlFor={`first-deposit-false-${index}`}>
													เคย
												</Label>
											</div>
											<div className="flex items-center space-x-2">
												<RadioGroupItem
													value="true"
													id={`first-deposit-true-${index}`}
												/>
												<Label htmlFor={`first-deposit-true-${index}`}>
													ไม่เคย
												</Label>
											</div>
										</RadioGroup>
									</div>
								)
							}

							if (
								constraint.constraintType ===
								PromotionType.DEPOSIT_HISTORY_AMOUNT
							) {
								return (
									<div key={index} className="space-y-2">
										<FormLabel>เงื่อนไขประวัติยอดฝากสะสม</FormLabel>
										<div className="grid grid-cols-3 gap-4">
											<FormItem className="flex-1">
												<FormLabel>ช่วงเวลา</FormLabel>
												<Select
													value={constraint.period}
													onValueChange={(value: DepositHistoryPeriod) => {
														const newValue = [...field.value]
														newValue[index] = {
															...constraint,
															period: value,
														}
														field.onChange(newValue)
													}}
												>
													<SelectTrigger>
														<SelectValue placeholder="เลือกช่วงเวลา" />
													</SelectTrigger>
													<SelectContent>
														{Object.values(DepositHistoryPeriod).map(
															(value) => (
																<SelectItem key={value} value={value}>
																	{depositHistoryPeriodMapper[value]}
																</SelectItem>
															),
														)}
													</SelectContent>
												</Select>
											</FormItem>

											<FormItem className="flex-1">
												<FormLabel>เงื่อนไข</FormLabel>
												<Select
													value={constraint.operator}
													onValueChange={(value: ComparisonOperator) => {
														const newValue = [...field.value]
														newValue[index] = {
															...constraint,
															operator: value,
														}
														field.onChange(newValue)
													}}
												>
													<SelectTrigger>
														<SelectValue placeholder="เลือกเงื่อนไข" />
													</SelectTrigger>
													<SelectContent>
														<SelectItem value={ComparisonOperator.GREATER_THAN}>
															มากกว่า
														</SelectItem>
														<SelectItem
															value={ComparisonOperator.GREATER_THAN_OR_EQUAL}
														>
															มากกว่าหรือเท่ากับ
														</SelectItem>
													</SelectContent>
												</Select>
											</FormItem>

											<FormItem className="flex-1">
												<FormLabel>จำนวนเงิน (บาท)</FormLabel>
												<Input
													type="number"
													onKeyDown={preventMinusInput}
													value={constraint.amount ?? ''}
													onChange={(e) => {
														const newValue = [...field.value]
														newValue[index] = {
															...constraint,
															amount: e.target.value
																? Number(e.target.value)
																: null,
														}
														field.onChange(newValue)
													}}
													placeholder="กรอกจำนวนเงิน"
												/>
											</FormItem>
										</div>
									</div>
								)
							}

							return null
						})}
					</div>
					{fieldState.error && (
						<FormMessage>{fieldState.error.message}</FormMessage>
					)}
				</FormItem>
			)}
		/>
	)
}
